import { isEmpty } from 'underscore';
import { glom } from './glom';

/**
 * Get value of Override parameter by name
 *
 * URL Overrides are similar to parameters found in a query string. The
 * differences are that Overrides can use `-` instead of `=` to separate the
 * parameter name and value. Additionally, Overrides can be found in either the
 * query string or the fragment after the hash mark (`#`).
 *
 * *Note:* Be careful when using `-` as part of the parameter name, as you may
 * encounter conflicts. For example if you used an override named 'window' and
 * another named 'window-title', the parsed value for 'window' could incorrectly
 * pull in 'window-title'.
 *
 * @example
 * ```javascript
 * const url = 'http://example.com?name1-value1&name2-value2#name3-value3';
 * const name1 = getOverrideByName( 'name1', url );
 * const name2 = getOverrideByName( 'name2', url );
 * const name3 = getOverrideByName( 'name3', url );
 * ```
 *
 * @param name
 * @param url (default: `window.location.href`)
 * @returns the value if found, `null` if not found, and an empty string if
 * found but no value is set.
 */
export function getOverrideByName(name: string, url: string = window.location.href): string | null {
    const escapedName = name.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp('[?&#]' + escapedName + '([-=]([^&#]*)|&|#|$)');
    const results = regex.exec(url);
    if (!results) {
        return null;
    }
    if (!results[2]) {
        return '';
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

/**
 * Determine if the Signage was initiated from within the CMS (i.e. a Preview).
 *
 * @param url (default: `window.location.href`)
 */
export function isInsideCms(url?: string): boolean {
    const postbackURL = getPostbackUrl(url);
    return postbackURL !== null;
}

/**
 * Retrieve the URL the Signage should post back to when it needs to communicate
 * with the CMS that initiated it.
 *
 * @param url (default: `window.location.href`)
 */
export function getPostbackUrl(url?: string): string | null {
    const postbackValue = getOverrideByName('postback', url);
    if (isEmpty(postbackValue)) {
        return null;
    }
    return postbackValue;
}

/**
 * Convert Override value to boolean.
 *
 * String values like 'on', 'yes', 'true', and '1' will return true.
 * Additionally empty String will also return as `true`.  Everything else will
 * return as `false`.
 */
function overrideToBoolean(value: string | null): boolean {
    if (value === null) {
        return false;
    }
    return ['', 'on', 'yes', 'true', '1'].includes(value.toLowerCase());
}

/**
 * Determine if the mouse cursor should be shown.
 *
 * @param url (default: `window.location.href`)
 */
export function allowCursorToBeShown(url?: string): boolean {
    const allowCursorValue = getOverrideByName('allow-cursor', url);
    return overrideToBoolean(allowCursorValue);
}

/**
 * Determine if the Override value is populated.
 */
function isNotEmpty(value: string | null): value is string {
    return !isEmpty(value);
}

/**
 * Detect URL Anchor Overrides.
 *
 * Search the URL for variables to be saved.
 */
export function detectUrlAnchorOverrides(): void {
    const localeValue = getOverrideByName('locale');
    if (isNotEmpty(localeValue)) {
        glom.locale = localeValue;
    }

    const scaleValue = getOverrideByName('scale');
    if (isNotEmpty(scaleValue)) {
        glom.windowScale = scaleValue;
    }

    glom.insideCMS = isInsideCms(window.location.href);

    glom.postbackURL = getPostbackUrl(window.location.href);

    const windowTitleValue = getOverrideByName('window-title');
    if (isNotEmpty(windowTitleValue)) {
        glom.titleId = windowTitleValue;
    }

    const maxLoopSlotsValue = getOverrideByName('max-loop-slots');
    if (isNotEmpty(maxLoopSlotsValue)) {
        glom.maxLoopSlots = parseInt(maxLoopSlotsValue);
    }

    glom.allowCursor = allowCursorToBeShown();
}

/**
 * The temperature scale (Fahrenheit, Celsius, etc.) that should be used.
 *
 * @param url (default: `window.location.href`)
 */
export function getTemperatureScale(url?: string): string | null {
    const scaleValue = getOverrideByName('temperature-scale', url);
    if (isEmpty(scaleValue)) {
        return null;
    }
    return scaleValue;
}
